import React, { useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { toast } from 'react-hot-toast';
import { systemDocumentApi } from '../api/systemRegisterApi';
import { formatEnumLabel } from '../constants/enums';
import { apiErrorMessage } from '../utils/responseHelpers';
import SystemDocumentDialog, { emptySystemDocumentRow } from './SystemDocumentDialog';

export { emptySystemDocumentRow };

/**
 * System documents register: table + add/edit dialog (Compliance & Documentation).
 */
export default function SystemDocumentsTable({ rows = [], onChange, error }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const primary = theme.palette.primary.main;
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRow, setEditingRow] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [openingKey, setOpeningKey] = useState(null);

  const openAdd = () => {
    setEditingRow(emptySystemDocumentRow());
    setDialogOpen(true);
  };

  const openEdit = (row) => {
    setEditingRow({ ...emptySystemDocumentRow(), ...row });
    setDialogOpen(true);
  };

  const handleSave = (row) => {
    const list = [...(rows || [])];
    const idx = list.findIndex((r) => r.key === row.key || (row.id != null && r.id === row.id));
    if (idx >= 0) {
      list[idx] = row;
    } else {
      list.push(row);
    }
    onChange(list);
    setDialogOpen(false);
    setEditingRow(null);
  };

  const confirmDelete = () => {
    if (!pendingDelete?.key) return;
    onChange((rows || []).filter((r) => r.key !== pendingDelete.key));
    setPendingDelete(null);
  };

  const handleOpenDocument = async (row) => {
    if (!row?.location) {
      toast.error('No file attached to open.');
      return;
    }
    setOpeningKey(row.key);
    try {
      const response = await systemDocumentApi.fetchFile(row.location);
      const contentType =
        row.contentType
        || response.headers?.['content-type']
        || 'application/octet-stream';
      const blob = new Blob([response.data], { type: contentType });
      const url = URL.createObjectURL(blob);
      const opened = window.open(url, '_blank', 'noopener,noreferrer');
      if (!opened) {
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.target = '_blank';
        anchor.rel = 'noopener noreferrer';
        anchor.download = row.fileName || 'document';
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
      }
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Failed to open document'));
    } finally {
      setOpeningKey(null);
    }
  };

  return (
    <Box>
      <Box
        display="flex"
        alignItems={{ xs: 'stretch', sm: 'center' }}
        justifyContent="space-between"
        gap={1.5}
        flexDirection={{ xs: 'column', sm: 'row' }}
        mb={1}
      >
        <Typography variant="subtitle2" fontWeight={700}>
          System documents
        </Typography>
        <Box sx={{ width: { xs: '100%', sm: 280 }, flexShrink: 0 }}>
          <Button
            size="small"
            variant="contained"
            color="secondary"
            startIcon={<AddIcon />}
            onClick={openAdd}
            fullWidth
          >
            Add document
          </Button>
        </Box>
      </Box>

      {error ? (
        <Typography variant="caption" color="error" display="block" mb={1}>
          {error}
        </Typography>
      ) : null}

      <TableContainer
        component={Paper}
        variant="outlined"
        sx={{
          borderRadius: 2,
          borderColor: alpha(primary, 0.18),
          bgcolor: isDark ? 'background.paper' : alpha('#fff', 0.9),
        }}
      >
        <Table size="small">
          <TableHead>
            <TableRow
              sx={{
                bgcolor: alpha(primary, isDark ? 0.18 : 0.08),
                '& th': { fontWeight: 700, whiteSpace: 'nowrap' },
              }}
            >
              <TableCell>Document name</TableCell>
              <TableCell>Document type</TableCell>
              <TableCell>Deployment status</TableCell>
              <TableCell>Attached file</TableCell>
              <TableCell align="right" width={96}>
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(rows ?? []).length === 0 ? (
              <TableRow>
                <TableCell colSpan={5}>
                  <Typography variant="body2" color="text.secondary" py={1.5}>
                    No documents yet. Click Add document to attach one.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => {
                const opening = openingKey === row.key;
                return (
                  <TableRow key={row.key || row.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>
                        {row.documentName || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {row.documentType ? formatEnumLabel(row.documentType) : '—'}
                    </TableCell>
                    <TableCell>
                      {row.deploymentStatus
                        ? formatEnumLabel(row.deploymentStatus)
                        : '—'}
                    </TableCell>
                    <TableCell>
                      {row.fileName ? (
                        row.location ? (
                          <Link
                            component="button"
                            type="button"
                            variant="body2"
                            underline="hover"
                            disabled={opening}
                            onClick={() => handleOpenDocument(row)}
                            sx={{
                              cursor: opening ? 'wait' : 'pointer',
                              textAlign: 'left',
                              border: 'none',
                              background: 'none',
                              p: 0,
                              font: 'inherit',
                            }}
                          >
                            {opening ? 'Opening…' : row.fileName}
                          </Link>
                        ) : (
                          <Typography variant="body2">{row.fileName}</Typography>
                        )
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          No file
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => openEdit(row)}>
                          <EditOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Remove">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() =>
                            setPendingDelete({
                              key: row.key,
                              documentName: row.documentName,
                              fileName: row.fileName,
                            })
                          }
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <SystemDocumentDialog
        open={dialogOpen}
        initialValues={editingRow}
        onClose={() => {
          setDialogOpen(false);
          setEditingRow(null);
        }}
        onSave={handleSave}
      />

      <Dialog
        open={Boolean(pendingDelete)}
        onClose={() => setPendingDelete(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Delete document?</DialogTitle>
        <DialogContent>
          <DialogContentText>Do you want to delete the document?</DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setPendingDelete(null)} color="inherit">
            Cancel
          </Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
